import { EXCHANGES } from "@cede/utils";
import { useCryptocomTickerWS } from "@/hooks/exchanges/direct-ws/cryptocom/useCryptocomTickerWS";
import { useKatanaTickerWS } from "@/hooks/exchanges/direct-ws/katana/useKatanaTickerWS";
import type { RealtimeTickerData } from "@/hooks/exchanges/types";

interface UseRealtimeExchangeTickerDataParams {
  exchangeId: string | undefined;
  pairSymbol: string | undefined;
  enabled?: boolean;
}

interface UseRealtimeExchangeTickerDataResult {
  ticker: RealtimeTickerData | undefined;
  isConnected: boolean;
  error: string | null;
}

/**
 * Exchange-agnostic hook for real-time ticker data via WebSocket.
 * Dispatches to the appropriate exchange-specific WS hook under the hood.
 *
 * Currently supported: Crypto.com (perps), Katana (perps).
 * Unsupported exchanges return undefined ticker (consumer should fallback to REST data).
 */
export const useRealtimeExchangeTickerData = ({
  exchangeId,
  pairSymbol,
  enabled = true,
}: UseRealtimeExchangeTickerDataParams): UseRealtimeExchangeTickerDataResult => {
  const isCryptocom = exchangeId === EXCHANGES.CRYPTOCOM;
  const isKatana = exchangeId === EXCHANGES.KATANA;

  const {
    ticker: cryptocomTicker,
    isConnected: cryptocomIsConnected,
    error: cryptocomError,
  } = useCryptocomTickerWS({
    pairSymbol,
    enabled: enabled && isCryptocom && !!pairSymbol,
  });

  const {
    ticker: katanaTicker,
    isConnected: katanaIsConnected,
    error: katanaError,
  } = useKatanaTickerWS({
    pairSymbol,
    enabled: enabled && isKatana && !!pairSymbol,
  });

  if (isCryptocom) {
    return { ticker: cryptocomTicker, isConnected: cryptocomIsConnected, error: cryptocomError };
  }
  if (isKatana) {
    return { ticker: katanaTicker, isConnected: katanaIsConnected, error: katanaError };
  }

  return {
    ticker: undefined,
    isConnected: false,
    error: null,
  };
};
